import axios from 'axios';
import { getLegacyRedirectPath } from '../utils/legacyRedirectPath';

// Detect if we're running on localhost for development
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const API_BASE_URL = isLocalhost 
  ? '' // Use Vite proxy in development
  : ''; // Same domain in production

let showRedirectsCache = null;

const WATCHLIST_KEY = "tv-releases-watchlist";

export const clearCache = () => {
  try {
    const watchlist = localStorage.getItem(WATCHLIST_KEY);
    localStorage.clear();
    if (watchlist) {
      localStorage.setItem(WATCHLIST_KEY, watchlist);
    }
    console.log('Cache cleared successfully (watchlist preserved)');
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

// Map dateRange to static JSON file path
const getStaticApiPath = (dateRange) => {
  if (dateRange === 'previous') {
    return '/api/tv-releases-previous.json';
  }
  return '/api/tv-releases-next.json';
};

const normalizeReleasePayload = (payload) => ({
  data: payload.data,
  referenceDay: typeof payload.referenceDay === 'string' ? payload.referenceDay : null,
  lastUpdated: payload.lastUpdated || null,
  count: typeof payload.count === 'number' ? payload.count : payload.data.length,
  totalCount: typeof payload.totalCount === 'number' ? payload.totalCount : payload.data.length,
  candidateCount: typeof payload.candidateCount === 'number' ? payload.candidateCount : null,
  stats: payload.stats || null,
  previousStats: payload.previousStats || null,
  previousLastUpdated: payload.previousLastUpdated || null,
  previousTotalCount: typeof payload.previousTotalCount === 'number' ? payload.previousTotalCount : null,
  monthIndex: payload.monthIndex || null,
  ifYouLikedPairings: Array.isArray(payload.ifYouLikedPairings) ? payload.ifYouLikedPairings : [],
});

const getStaticMonthApiPath = (yearMonth) => `/api/month/${encodeURIComponent(yearMonth)}.json`;
const getStaticBestOfApiPath = (yearMonth) => `/api/best-of/${encodeURIComponent(yearMonth)}.json`;

// Fetch releases - tries static JSON first, falls back to dynamic API
export const fetchReleases = async ({ dateRange = 'next' } = {}) => {
  const fetchDynamicReleases = async () => {
    const url = `${API_BASE_URL}/api/tv-releases`;

    const response = await axios.get(url, {
      params: { dateRange },
      timeout: 10000,
    });

    const payload = response.data;
    if (!payload || !Array.isArray(payload.data)) {
      console.error('Invalid response structure:', payload);
      throw new Error('Invalid data format received from backend API');
    }

    return normalizeReleasePayload(payload);
  };

  if (isLocalhost) {
    try {
      return await fetchDynamicReleases();
    } catch (error) {
      console.error('Error fetching releases from local backend:', error);
      throw error;
    }
  }

  // Try static JSON first (for Netlify deployment)
  const staticPath = getStaticApiPath(dateRange);
  try {
    const staticResponse = await axios.get(staticPath, { timeout: 5000 });
    const payload = staticResponse.data;
    if (payload && Array.isArray(payload.data)) {
      return normalizeReleasePayload(payload);
    }
  } catch {
    // Static file not available, fall back to dynamic API
  }

  // Fall back to dynamic API (for development or DO deployment)
  try {
    return await fetchDynamicReleases();
  } catch (error) {
    console.error('Error fetching releases from backend:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      response: error.response?.data,
    });
    throw error;
  }
};

const loadShowRedirects = async () => {
  if (!showRedirectsCache) {
    const redirectsResponse = await axios.get('/api/show-redirects.json', { timeout: 6000 });
    showRedirectsCache = redirectsResponse.data || {};
  }
  return showRedirectsCache;
};

// Where a saved show stands now: still listed, listed under a new slug, or taken down.
// A HEAD request on the static show payload reads only the status: 2xx means listed, 404 sends us to the
// redirects map (a retired slug appears there; a slug in neither was taken down). Any other status or a
// network error counts as listed, so a failed request never marks a show gone.
export const checkShowSlug = async (slug) => {
  try {
    await axios.head(`/api/show/${encodeURIComponent(slug)}.json`, { timeout: 6000 });
    return { status: 'listed' };
  } catch (error) {
    if (error.response?.status !== 404) {
      return { status: 'listed' };
    }
  }

  try {
    const redirects = await loadShowRedirects();
    if (!redirects || typeof redirects !== 'object') {
      return { status: 'listed' };
    }
    const target = redirects[slug];
    return target ? { status: 'moved', slug: target } : { status: 'gone' };
  } catch {
    return { status: 'listed' };
  }
};

export const fetchShowBySlug = async (slug) => {
  const encodedSlug = encodeURIComponent(slug);
  const fetchDynamicShow = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/show/${encodedSlug}`, {
      timeout: 10000,
    });
    return response.data;
  };

  if (isLocalhost) {
    try {
      return await fetchDynamicShow();
    } catch (error) {
      if (error.response?.status === 404) {
        return { notFound: true };
      }
      console.error(`Error fetching show by slug (${slug}):`, error);
      throw error;
    }
  }

  // Try static show payload first (for static deployment)
  try {
    const staticResponse = await axios.get(`/api/show/${encodedSlug}.json`, { timeout: 6000 });
    const payload = staticResponse.data;
    if (payload && payload.data) {
      return payload;
    }
  } catch {
    // Fall through to static redirects map, then dynamic API.
  }

  // Resolve legacy slug redirects from static map when available.
  try {
    const redirectSlug = (await loadShowRedirects())?.[slug];
    if (redirectSlug) {
      return { redirectTo: getLegacyRedirectPath(redirectSlug) };
    }
  } catch {
    // Ignore and fall back to dynamic API.
  }

  try {
    return await fetchDynamicShow();
  } catch (error) {
    if (error.response?.status === 404) {
      return { notFound: true };
    }
    console.error(`Error fetching show by slug (${slug}):`, error);
    throw error;
  }
};

export const fetchMonthReleases = async (yearMonth) => {
  if (!yearMonth) {
    throw new Error("yearMonth is required");
  }

  const fetchDynamicMonth = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/tv-releases/month/${encodeURIComponent(yearMonth)}`, {
      timeout: 10000,
    });
    return response.data;
  };

  if (isLocalhost) {
    return fetchDynamicMonth();
  }

  try {
    const staticResponse = await axios.get(getStaticMonthApiPath(yearMonth), { timeout: 6000 });
    const payload = staticResponse.data;
    if (payload && Array.isArray(payload.data)) {
      return payload;
    }
  } catch {
    // Fall through to dynamic endpoint.
  }

  return fetchDynamicMonth();
};

export const fetchMonthIndex = async ({ preferStatic = false } = {}) => {
  const fetchStaticMonthIndex = async () => {
    const staticResponse = await axios.get('/api/month-index.json', { timeout: 6000 });
    if (Array.isArray(staticResponse.data?.months)) {
      return staticResponse.data;
    }
    throw new Error('Invalid static month index payload');
  };

  if (preferStatic) {
    try {
      return await fetchStaticMonthIndex();
    } catch {
      // Fall through to dynamic endpoint.
    }
  }

  try {
    const response = await axios.get(`${API_BASE_URL}/api/month-index`, { timeout: 10000 });
    if (Array.isArray(response.data?.months)) {
      return response.data;
    }
  } catch {
    // Fall back to static snapshot for static-only deployments.
  }

  try {
    return await fetchStaticMonthIndex();
  } catch {
    // No static snapshot available.
  }

  throw new Error('Failed to load month index from dynamic API and static snapshot');
};

export const fetchBestOfReleases = async (yearMonth) => {
  if (!yearMonth) {
    throw new Error("yearMonth is required");
  }

  try {
    const staticResponse = await axios.get(getStaticBestOfApiPath(yearMonth), { timeout: 6000 });
    const payload = staticResponse.data;
    if (payload && Array.isArray(payload.data)) {
      return payload;
    }
  } catch {
    // Fall through to dynamic endpoint.
  }

  const response = await axios.get(`${API_BASE_URL}/api/best-of/${encodeURIComponent(yearMonth)}`, {
    timeout: 10000,
  });
  return response.data;
};

export const fetchMostRecommendedShows = async () => {
  const fetchDynamic = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/most-recommended`, {
      timeout: 10000,
    });
    return response.data;
  };

  const fetchStatic = async () => {
    const staticResponse = await axios.get('/api/most-recommended.json', { timeout: 6000 });
    return staticResponse.data;
  };

  const shouldPreferDynamic = isLocalhost;
  const sources = shouldPreferDynamic
    ? [fetchDynamic, fetchStatic]
    : [fetchStatic, fetchDynamic];

  for (const fetchSource of sources) {
    try {
      const payload = await fetchSource();
      if (payload && Array.isArray(payload.data)) {
        return payload;
      }
    } catch {
      // Try the next available source.
    }
  }

  throw new Error('Failed to load most recommended shows');
};

export const fetchShowsLikeBySlug = async (slug) => {
  if (!slug) {
    throw new Error('slug is required');
  }

  const encodedSlug = encodeURIComponent(slug);
  const fetchDynamic = async () => {
    const response = await axios.get(`${API_BASE_URL}/api/shows-like/${encodedSlug}`, {
      timeout: 10000,
    });
    return response.data;
  };

  const fetchStatic = async () => {
    const staticResponse = await axios.get(`/api/shows-like/${encodedSlug}.json`, { timeout: 6000 });
    return staticResponse.data;
  };

  const sources = isLocalhost
    ? [fetchDynamic, fetchStatic]
    : [fetchStatic, fetchDynamic];
  let lastFatalError = null;

  for (const fetchSource of sources) {
    try {
      const payload = await fetchSource();
      if (payload?.anchor && Array.isArray(payload?.picks)) {
        return payload;
      }
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 410) {
        continue;
      }
      lastFatalError = error;
    }
  }

  if (lastFatalError) {
    throw lastFatalError;
  }

  return { notFound: true };
};

// Health check endpoint
export const checkBackendHealth = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/health`, { timeout: 5000 });
    return response.data;
  } catch (error) {
    try {
      const staticResponse = await axios.get('/api/health.json', { timeout: 5000 });
      return staticResponse.data;
    } catch {
      console.error('Backend health check failed:', error);
      throw error;
    }
  }
};
